import { Download, FileText, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Dialog, DialogContent } from '@/shared/components/ui/dialog'
import { toast } from '@/shared/lib/toast'

export default function QuoteDetailsModal({
  open,
  onOpenChange,
  quote,
  _isAdmin = false,
}) {
  const navigate = useNavigate()

  if (!quote) return null

  const handleExportPdf = () => {
    if (!quote._id) {
      toast.error('Quotation ID not available')
      return
    }

    onOpenChange(false)

    navigate(`/sales/quotes/preview?id=${quote._id}`, {
      state: {
        fromQuotationModal: true,
        quotationId: quote._id,
      },
    })
  }

  const products = quote.products || []
  const productsWithDesc = products.filter(
    (p) => typeof p.description === 'string' && p.description.trim().length > 0,
  )
  const productsWithoutDesc = products.filter(
    (p) =>
      !p.description ||
      typeof p.description !== 'string' ||
      p.description.trim().length === 0,
  )
  const isPartialDesc =
    products.length > 1 &&
    productsWithDesc.length > 0 &&
    productsWithoutDesc.length > 0

  const displayProducts = isPartialDesc
    ? [...productsWithDesc, ...productsWithoutDesc]
    : products

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-1.5rem)] max-w-[550px] overflow-y-auto rounded-2xl sm:rounded-[26px] border-0 p-4 sm:p-6 shadow-2xl [&>button]:hidden">
        <div>
          {/* Header */}
          <div className="mb-5 flex items-center justify-between gap-3">
            {/* Quote Number */}
            <div className="flex min-w-0 items-center gap-2 sm:gap-3">
              <FileText className="h-5 w-5 shrink-0 text-slate-700" />

              <h2 className="truncate text-base font-bold text-slate-900 sm:text-[17px]">
                {quote.quoteNo}
              </h2>
            </div>

            {/* Actions */}
            <div className="flex shrink-0 items-center gap-2 sm:gap-3">
              <Button
                onClick={handleExportPdf}
                className="h-9 rounded-xl bg-[#111827] px-3 text-xs font-medium text-white hover:bg-[#1f2937] sm:h-10 sm:px-5 sm:text-sm"
              >
                <Download className="mr-1.5 h-3.5 w-3.5 sm:mr-2 sm:h-4 sm:w-4" />
                <span>Export PDF</span>
              </Button>

              <button
                onClick={() => onOpenChange(false)}
                className="shrink-0 rounded-full p-1 text-slate-500 transition hover:bg-slate-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between rounded-xl sm:rounded-[22px] bg-[#F4F6FF] px-4 py-3 sm:px-6 sm:py-4">
            <div>
              <p className="text-[10px] font-medium uppercase tracking-[0.12em] text-slate-500">
                GRAND TOTAL
              </p>

              <h1 className="mt-1 text-xl sm:text-[26px] font-bold leading-none text-slate-900">
                ₹{quote.grandTotal?.toLocaleString('en-IN') || 0}
              </h1>
            </div>

            <div className="text-right">
              <p className="mb-2 text-[10px] font-medium uppercase tracking-[0.12em] text-slate-500">
                STATUS
              </p>

              <Badge
                className={`rounded-full border-0 px-4 py-1.5 text-xs font-semibold ${
                  quote.status?.toLowerCase() === 'sent'
                    ? 'bg-green-100 text-green-700 hover:bg-green-100'
                    : 'bg-amber-100 text-amber-700 hover:bg-amber-100'
                }`}
              >
                {quote.status?.toLowerCase() === 'sent' ? 'Sent' : 'Pending'}
              </Badge>
            </div>
          </div>

          {/* Details Grid */}
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <InfoCard title="CONTACT NAME" value={quote.contactName || '-'} />

            <InfoCard title="ACCOUNT NAME" value={quote.accountName || '-'} />

            <InfoCard
              title="QUOTE DATE"
              value={
                quote.quoteDate
                  ? new Date(quote.quoteDate).toLocaleDateString('en-IN')
                  : '-'
              }
            />

            <InfoCard
              title="VALID UNTIL"
              value={
                quote.validUntil
                  ? new Date(quote.validUntil).toLocaleDateString('en-IN')
                  : '-'
              }
            />
          </div>

          {/* Sales Person */}
          {quote.createdBy?.name ? (
            <div className="mt-3 rounded-[20px] border border-slate-200 px-4 py-3">
              <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500">
                SALES PERSON
              </p>

              <p className="mt-1 text-[15px] font-semibold text-slate-900">
                {quote.createdBy.name} ({quote.createdBy.email})
              </p>
            </div>
          ) : null}

          {/* Addresses */}
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <InfoCard
              title="SHIPPING ADDRESS"
              value={formatAddress(quote.shippingAddress)}
            />

            <InfoCard
              title="BILLING ADDRESS"
              value={formatAddress(quote.billingAddress)}
            />
          </div>

          {/* Products */}
          <div className="mt-4 rounded-[22px] border border-slate-200 p-4">
            <h3 className="mb-3 text-sm font-semibold text-slate-900">
              Products ({displayProducts.length})
            </h3>

            {displayProducts.length ? (
              displayProducts.map((item, index) => (
                <div
                  key={index}
                  className="mb-3 rounded-xl border border-slate-200 bg-slate-50/50 p-3.5 last:mb-0"
                >
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Product</span>
                    <span className="font-semibold text-slate-900">
                      {item.product}
                    </span>
                  </div>

                  {item.description ? (
                    <div className="mt-2 rounded-lg border border-slate-200/80 bg-white p-2 text-xs text-slate-600">
                      <span className="mb-0.5 block font-semibold text-slate-500">
                        Description:
                      </span>
                      <p className="whitespace-pre-wrap">{item.description}</p>
                    </div>
                  ) : isPartialDesc ? (
                    <div className="mt-2 rounded-lg border border-dashed border-slate-200 bg-white/60 p-2 text-xs text-slate-400">
                      <span className="mb-0.5 block font-semibold text-slate-400">
                        Description:
                      </span>
                      <p className="italic">No description</p>
                    </div>
                  ) : null}

                  <div className="mt-2 flex justify-between text-sm">
                    <span className="text-slate-500">HSN Code</span>
                    <span className="text-slate-700">{item.hsn || '-'}</span>
                  </div>

                  <div className="mt-2 flex justify-between text-sm">
                    <span className="text-slate-500">Quantity</span>
                    <span className="text-slate-700">{item.quantity}</span>
                  </div>

                  <div className="mt-2 flex justify-between text-sm">
                    <span className="text-slate-500">Unit Price</span>
                    <span className="text-slate-700">
                      ₹{item.unitPrice?.toLocaleString('en-IN')}
                    </span>
                  </div>

                  <div className="mt-2 flex justify-between text-sm">
                    <span className="text-slate-500">CGST / SGST</span>
                    <span className="text-slate-700">
                      {item.cgst}% / {item.sgst}%
                    </span>
                  </div>

                  <div className="mt-2 flex justify-between border-t pt-2 text-sm font-semibold text-slate-900">
                    <span>Total</span>
                    <span>₹{item.total?.toLocaleString('en-IN')}</span>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-slate-500">No products found.</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function InfoCard({ title, value }) {
  return (
    <div className="rounded-[20px] border border-slate-200 px-4 py-3">
      <p className="text-[10px] font-medium uppercase tracking-[0.12em] text-slate-500">
        {title}
      </p>

      <h3 className="mt-1 text-[15px] font-semibold leading-tight break-words text-slate-900">
        {value}
      </h3>
    </div>
  )
}

function formatAddress(address) {
  if (!address) return '-'

  return [
    address.line1,
    address.line2,
    address.city,
    address.state,
    address.pincode,
    address.country,
  ]
    .filter(Boolean)
    .join(', ')
}
